// Mind › Vision's own input: a drawing, a chosen image or the visitor's camera as the stimulus the
// cortex is shown. The source is drawn into one square canvas (centre-cropped; the field is square, 24° across,
// the 12° aperture its inscribed circle, as the run's `uv` has it), the strip paints that canvas and colours the
// centres by what they see in it, and ba-mind's live layer (pipelines/mind/retinotopy/live-layer.mjs, #663)
// samples the same square's luminance at each V1–V3 vertex's receptive-field centre and pushes it to the viewport's
// live vertex layer when the viewport has one (feature-detected), drawn with the run's own look (LIVE_LOOK: the
// drive ramp fading in from the cortex's shade by luminance, never black). The camera turns on only when
// the visitor switches it on, its frames stay in this browser, nothing is uploaded, and switching off stops the
// tracks. Mirroring is done once, into the square, so the strip and the cortex agree. The camera is paced by the
// video's own frame callback through one phase-preserving cap (createPacer), after ba-mind's diagnosis found the
// old display-tick cap losing a frame in three (research/2026-10-03-mind-live-pacing-diagnosis.md).

import { create } from 'zustand';
import { createRetinotopyLayer, supportsLiveRetinotopy, type RetinotopyLayer, type RetinotopyLook } from '../../../../pipelines/mind/retinotopy/live-layer.mjs';
import type { Viewport } from '../viewport/api.ts';
import { RAMPS, type Stops } from '../ui/scales.ts';
import { clearPaintPixels, paintSegment, type FieldPoint } from './mindPaint.ts';
import { drawWave, WAVE, waveValue, waveValueAt, type WaveKind } from './mindWave.ts';

/** The square the sources are drawn into: plenty for 20,484 centres and the 168 px strip. */
export const SOURCE_PX = 256;
/** The camera's frame rate into the square, and the live layer's own gate. */
export const CAMERA_HZ = 30;

/** A frame cap that carries its remainder: a token bucket filling at the cap's rate, holding at most `burst` frames.
 *  A frame goes when a whole token is there, so two 16.6 ms display ticks totalling 33.2 ms don't lose a frame to the
 *  tick after (the 0.996 of a token they bring carries over), a source at the cap's own rate with jitter keeps its
 *  frames, a burst of ticks after a stall (a page rendering in bursts) still gets its fresh frames, and the long-run rate
 *  stays under the cap (ba-mind's diagnosis, research/2026-10-03-mind-live-pacing-diagnosis.md: the old
 *  `now − last < 1000/30` with `last = now` admitted about 22 of a 30 Hz source's frames a second and waited to ~50 ms on
 *  the rest). */
export interface Pacer {
  /** Whether a frame at `nowMs` goes; a yes spends a token. */
  admit(nowMs: number): boolean;
  /** The tokens in hand, for tests. */
  readonly tokens: number;
}

export function createPacer(hz: number, burst = 2): Pacer {
  const interval = 1000 / hz;
  // A token short by rounding (a few nanoseconds of a 33 ms interval) is a whole token: it must not cost a frame.
  const whole = 1 - 1e-6;
  let tokens = burst;
  let lastMs = -Infinity;
  return {
    admit(nowMs) {
      if (!Number.isFinite(nowMs)) return false;
      if (lastMs !== -Infinity) tokens = Math.min(burst, tokens + Math.max(0, nowMs - lastMs) / interval);
      lastMs = nowMs;
      if (tokens < whole) return false;
      tokens = Math.max(0, tokens - 1);
      return true;
    },
    get tokens() {
      return tokens;
    },
  };
}

/** A source's presented frames, sampled once each: a trigger (a display tick, or the video's frame callback) brings the
 *  frame's id (the video's presented-frame count, where the browser counts); a frame already sampled is skipped, a fresh
 *  one asks the pacer. Pure, for the tests. */
export interface FrameSampler {
  take(nowMs: number, frameId: number | undefined): boolean;
}

export function createFrameSampler(hz: number): FrameSampler {
  const pacer = createPacer(hz);
  let last: number | undefined;
  return {
    take(nowMs, frameId) {
      if (frameId !== undefined && frameId === last) return false;
      if (!pacer.admit(nowMs)) return false;
      last = frameId;
      return true;
    },
  };
}

/** `requestVideoFrameCallback` and `getVideoPlaybackQuality`, where the browser has them (Chrome, Safari; Firefox has the
 *  callback behind a flag). */
interface FrameMeta {
  presentedFrames?: number;
  mediaTime?: number;
}
type VideoWithFrameCallbacks = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: FrameMeta) => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
  getVideoPlaybackQuality?: () => { totalVideoFrames: number };
};
/** The live layer's look: the run's own bar's (the engine's activity overlay on design's drive ramp, data/sims/layers.ts).
 *  Luminance is the overlay's weight (`rise [0, 1]`): a dark point leaves the cortex its own shade and shading, a bright one
 *  takes 70 % of the ramp's colour and its glow, and nothing is black (ba-look's call through the lead, 10-03: before, the
 *  frame's RGB replaced the cortex's colour, so a dark camera frame read as holes in the occipital pole). */
export const LIVE_LOOK: RetinotopyLook = {
  colormap: { stops: RAMPS.drive as Stops },
  range: [0, 1],
  blend: 'mix',
  albedo: 0.7,
  emission: 0.6,
  rise: [0, 1],
};

export type SourceKind = 'image' | 'camera' | 'paint' | WaveKind;
export type LiveStatus = 'off' | 'starting' | 'on' | 'error';

interface MindLiveState {
  kind: SourceKind | null;
  status: LiveStatus;
  error: string | null;
  /** The source drawn left-to-right flipped, as a mirror shows a face. */
  mirror: boolean;
  /** Frames drawn into the square so far: the strip repaints on each. */
  frames: number;
  /** Whether the viewport takes live vertex layers; null until a source first asked. */
  pushed: boolean | null;
  /** The drawing caption, image's file name or camera's label. */
  label: string | null;
  /** A source a link asked for (`live=paint`) before the lookup was bound; the panel starts it once it binds, and it clears when
   *  the source is on or fails, or when the visitor goes back to the bar. */
  wanted: SourceKind | null;
  waveValue: number;
  wavePlaying: boolean;
}

export const useMindLive = create<MindLiveState>(() => ({ kind: null, status: 'off', error: null, mirror: false, frames: 0, pushed: null, label: null, wanted: null, waveValue: 45, wavePlaying: false }));

/** A link's source request (today `live=paint`); null withdraws it. */
export function requestSource(kind: SourceKind | null): void {
  if (useMindLive.getState().wanted !== kind) useMindLive.setState({ wanted: kind });
}

if (typeof window !== 'undefined') (window as unknown as { __mindLive?: typeof useMindLive }).__mindLive = useMindLive;

/** The centred square of a w × h source, in source pixels. */
export function squareCrop(w: number, h: number): { sx: number; sy: number; size: number } {
  const size = Math.max(1, Math.min(w, h));
  return { sx: (w - size) / 2, sy: (h - size) / 2, size };
}

/** The same bilinear display luma as the cortical layer, with alpha over black.
 * The source is already the cropped square. Field markers must agree at ink edges. */
export function luminanceAt(img: { width: number; height: number; data: ArrayLike<number> }, u: number, v: number): number {
  if (!(u >= 0 && u <= 1 && v >= 0 && v <= 1)) return 0;
  const x = u * (img.width - 1), y = v * (img.height - 1);
  const x0 = Math.floor(x), y0 = Math.floor(y), a = x - x0, b = y - y0;
  let value = 0;
  for (let j = 0; j < 2; j++) for (let k = 0; k < 2; k++) {
    const i = 4 * (Math.min(y0 + j, img.height - 1) * img.width + Math.min(x0 + k, img.width - 1));
    value += (k ? a : 1 - a) * (j ? b : 1 - b) *
      (0.2126 * img.data[i]! + 0.7152 * img.data[i + 1]! + 0.0722 * img.data[i + 2]!) * img.data[i + 3]! / 65025;
  }
  return value;
}

/** The run's arrays the live layer binds to: fsaverage5's 20,484 vertices, LH then RH. */
export interface LiveBinding {
  uv: Float32Array;
  valid: Uint8Array;
}

// One square for the page, and the source behind it.
let square: HTMLCanvasElement | null = null;
let lastImage: ImageData | null = null;
let still: ImageBitmap | null = null;
let video: HTMLVideoElement | null = null;
let stream: MediaStream | null = null;
let raf = 0;
let vfc = 0;
let layer: RetinotopyLayer | null = null;
let layerFor: Viewport | null = null;
let binding: LiveBinding | null = null;
let viewport: Viewport | null = null;
let generation = 0;
let paintImage: ImageData | null = null;
let paintRaf = 0;
let paintDirty = false;
let waveImage: ImageData | null = null;
let waveRaf = 0;
let waveEpoch = 0;
// Layer creation/removal must finish in order: a late removal must not erase a new source's layer.
let layerWork: Promise<unknown> = Promise.resolve();
let layerEpoch = 0;

/** The square the strip paints, once a source has drawn into it. */
export const liveSquare = (): HTMLCanvasElement | null => (useMindLive.getState().frames > 0 ? square : null);
/** The last frame drawn, for sampling what each centre sees. */
export const liveImage = (): ImageData | null => lastImage;

function ensureSquare(): CanvasRenderingContext2D {
  if (!square) {
    square = document.createElement('canvas');
    square.width = square.height = SOURCE_PX;
  }
  const ctx = square.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2D canvas');
  return ctx;
}

/** Draw a source into the square, centre-cropped, mirrored if asked; the frame counter moves. */
function drawInto(src: CanvasImageSource, w: number, h: number, mirror: boolean): ImageData {
  const ctx = ensureSquare();
  const { sx, sy, size } = squareCrop(w, h);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, SOURCE_PX, SOURCE_PX);
  if (mirror) ctx.setTransform(-1, 0, 0, 1, SOURCE_PX, 0);
  ctx.drawImage(src, sx, sy, size, size, 0, 0, SOURCE_PX, SOURCE_PX);
  ctx.restore();
  lastImage = ctx.getImageData(0, 0, SOURCE_PX, SOURCE_PX);
  useMindLive.setState((s) => ({ frames: s.frames + 1 }));
  return lastImage;
}

/** The live layer on the viewport, made once per viewport and binding when it takes live layers. */
function ensureLayer(): Promise<RetinotopyLayer | null> {
  const vp = viewport, b = binding, epoch = layerEpoch;
  const work = layerWork.then(async () => {
    if (!vp || !b || epoch !== layerEpoch) return null;
    if (layer && layerFor === vp) return layer;
    if (!supportsLiveRetinotopy(vp)) {
      useMindLive.setState({ pushed: false });
      return null;
    }
    const next = await createRetinotopyLayer(vp, { uv: b.uv, valid: b.valid, channel: 'luminance', maxHz: CAMERA_HZ, look: LIVE_LOOK });
    if (epoch !== layerEpoch) {
      await next.dispose();
      return null;
    }
    layer = next;
    layerFor = vp;
    useMindLive.setState({ pushed: true });
    return layer;
  });
  layerWork = work.catch(() => undefined);
  return work;
}

/** The viewport and the run's arrays a source will bind to; call before a source starts. */
export function bindLive(vp: Viewport | null, b: LiveBinding | null) {
  if (viewport !== vp || binding !== b) void stopLive();
  viewport = vp;
  binding = b;
}

/** A still image: drawn once, pushed once (forced past the layer's rate gate). */
export async function startImage(file: Blob, name: string): Promise<void> {
  const gen = ++generation;
  stopSource();
  useMindLive.setState({ kind: 'image', status: 'starting', error: null, label: name, frames: 0 });
  try {
    const bmp = await createImageBitmap(file);
    if (gen !== generation) { bmp.close(); return; }
    still = bmp;
    const img = drawInto(bmp, bmp.width, bmp.height, useMindLive.getState().mirror);
    const l = await ensureLayer();
    if (gen !== generation) return;
    l?.push(img, { force: true });
    useMindLive.setState({ status: 'on' });
  } catch (e) {
    if (gen === generation) useMindLive.setState({ status: 'error', error: e instanceof Error ? e.message : String(e) });
  }
}

/** The camera, only on the visitor's switch: frames into the square at CAMERA_HZ, each pushed when the layer wants one. */
export async function startCamera(): Promise<void> {
  const gen = ++generation;
  stopSource();
  useMindLive.setState({ kind: 'camera', status: 'starting', error: null, label: 'Camera', frames: 0 });
  try {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('this browser offers no camera here');
    const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
    if (gen !== generation) {
      for (const t of s.getTracks()) t.stop();
      return;
    }
    stream = s;
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.srcObject = s;
    video = v;
    await v.play();
    if (gen !== generation) return;
    const l = await ensureLayer();
    if (gen !== generation) return;
    useMindLive.setState({ status: 'on', label: s.getVideoTracks()[0]?.label || 'Camera' });
    // Frames at the source's own pace (ba-mind's diagnosis, research/2026-10-03-mind-live-pacing-diagnosis.md): every
    // display tick and, where the browser has it, the video's frame callback are triggers; whichever sees a fresh
    // presented frame first samples it, the frame's count keeping it from going twice, through one cap at CAMERA_HZ that
    // carries its remainder (a token bucket); a frame the cap admits goes past the layer's own gate (`force`), so two caps
    // don't compete. Both triggers, because a page rendering in bursts (a GPU-bound headless run) gets the callback once a
    // burst while the ticks inside it still see new frames, and the bucket's allowance lets those frames go.
    const vv = v as VideoWithFrameCallbacks;
    const sampler = createFrameSampler(CAMERA_HZ);
    const frameId = (meta?: FrameMeta): number | undefined => (typeof vv.getVideoPlaybackQuality === 'function' ? vv.getVideoPlaybackQuality().totalVideoFrames : meta?.presentedFrames);
    const show = (now: number, meta?: FrameMeta) => {
      if (gen !== generation || video !== v || v.readyState < 2 || !v.videoWidth || !sampler.take(now, frameId(meta))) return;
      const img = drawInto(v, v.videoWidth, v.videoHeight, useMindLive.getState().mirror);
      l?.push(img, { nowMs: now, force: true });
    };
    const tick = (now: number) => {
      if (gen !== generation || video !== v) return;
      raf = requestAnimationFrame(tick);
      show(now);
    };
    raf = requestAnimationFrame(tick);
    if (typeof vv.requestVideoFrameCallback === 'function') {
      const onFrame = (now: number, meta: FrameMeta) => {
        if (gen !== generation || video !== v) return;
        vfc = vv.requestVideoFrameCallback!(onFrame);
        show(now, meta);
      };
      vfc = vv.requestVideoFrameCallback(onFrame);
    }
  } catch (e) {
    if (gen === generation) useMindLive.setState({ status: 'error', error: e instanceof Error ? e.message : String(e), kind: null });
  }
}

/** Local synthetic sources validate the lookup before pixels, a layer or an upload. */
function requireLocalBinding(): void {
  if (!viewport || !binding || !(binding.uv instanceof Float32Array) || !(binding.valid instanceof Uint8Array) ||
      binding.uv.length !== 20484 * 2 || binding.valid.length !== 20484) throw new Error('The V1–V3 lookup is not ready');
  let count = 0;
  for (let i = 0; i < binding.valid.length; i++) {
    if (binding.valid[i] !== 0 && binding.valid[i] !== 1) throw new Error('Invalid V1–V3 mask');
    if (!binding.valid[i]) continue;
    const u = binding.uv[2 * i]!, v = binding.uv[2 * i + 1]!;
    if (!Number.isFinite(u) || !Number.isFinite(v) || u < 0 || u > 1 || v < 0 || v > 1) throw new Error('Invalid V1–V3 coordinates');
    count++;
  }
  if (!count) throw new Error('The V1–V3 mask is empty');
}

/** A drawing is a local bitmap, never a video stream. It does not inherit camera mirroring. */
export async function startPaint(): Promise<void> {
  const gen = ++generation;
  stopSource();
  useMindLive.setState({ kind: 'paint', status: 'starting', error: null, label: 'Your drawing', mirror: false, frames: 0 });
  try {
    requireLocalBinding();
    paintImage = ensureSquare().createImageData(SOURCE_PX, SOURCE_PX);
    clearPaintPixels(paintImage);
    await ensureLayer();
    if (gen !== generation) return;
    useMindLive.setState({ status: 'on', wanted: null });
    paintDirty = true;
    finishPaintStroke();
  } catch (e) {
    if (gen === generation) useMindLive.setState({ status: 'error', error: e instanceof Error ? e.message : String(e), wanted: null });
  }
}

/** Coalesce pointer changes, then force the admitted bitmap past the video layer's rate cap. */
export function paintStroke(from: FieldPoint, to: FieldPoint = from): void {
  if (useMindLive.getState().kind !== 'paint' || useMindLive.getState().status !== 'on' || !paintImage) return;
  if (!paintSegment(paintImage, from, to)) return;
  paintDirty = true;
  if (!paintRaf) {
    const gen = generation;
    paintRaf = requestAnimationFrame(() => {
      if (gen !== generation) return;
      paintRaf = 0;
      finishPaintStroke();
    });
  }
}

/** Also called on release/cancel: the final stroke cannot be left waiting for a callback. */
export function finishPaintStroke(): void {
  if (paintRaf) cancelAnimationFrame(paintRaf);
  paintRaf = 0;
  if (!paintDirty || !paintImage || useMindLive.getState().kind !== 'paint' || useMindLive.getState().status !== 'on') return;
  const ctx = ensureSquare();
  ctx.putImageData(paintImage, 0, 0);
  lastImage = ctx.getImageData(0, 0, SOURCE_PX, SOURCE_PX);
  layer?.push(lastImage, { force: true });
  paintDirty = false;
  useMindLive.setState((s) => ({ frames: s.frames + 1 }));
}

export function clearPaint(): void {
  if (!paintImage || useMindLive.getState().kind !== 'paint' || useMindLive.getState().status !== 'on') return;
  clearPaintPixels(paintImage);
  paintDirty = true;
  finishPaintStroke();
}

/** One local source: the field and cortex always see the same unmirrored bitmap. */
export async function startWave(kind: WaveKind): Promise<void> {
  const gen = ++generation;
  stopSource();
  useMindLive.setState({ kind, status: 'starting', error: null, label: kind === 'wedge' ? 'Wedge' : 'Ring', mirror: false, frames: 0, waveValue: WAVE[kind].initial });
  try {
    requireLocalBinding();
    waveImage = ensureSquare().createImageData(SOURCE_PX, SOURCE_PX);
    await ensureLayer();
    if (gen !== generation) return;
    useMindLive.setState({ status: 'on', wanted: null });
    setWaveValue(WAVE[kind].initial);
    // Explicit source selection starts Play unless the visitor asks for reduced motion.
    setWavePlaying(!(typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches));
  } catch (e) {
    if (gen === generation) {
      setWavePlaying(false);
      useMindLive.setState({ status: 'error', error: e instanceof Error ? e.message : String(e), wanted: null });
    }
  }
}

/** Synchronous final-value flush for keyboard, pointer and Play alike. */
export function setWaveValue(value: number): void {
  const s = useMindLive.getState();
  if ((s.kind !== 'wedge' && s.kind !== 'ring') || s.status !== 'on' || !waveImage || !Number.isFinite(value)) return;
  const next = waveValue(s.kind, value);
  if (s.frames > 0 && next === s.waveValue) return;
  drawWave(waveImage, s.kind, next);
  const ctx = ensureSquare();
  ctx.putImageData(waveImage, 0, 0);
  lastImage = ctx.getImageData(0, 0, SOURCE_PX, SOURCE_PX);
  layer?.push(lastImage, { force: true });
  useMindLive.setState({ waveValue: next, frames: s.frames + 1 });
}

export function setWavePlaying(on: boolean): void {
  ++waveEpoch;
  if (waveRaf) cancelAnimationFrame(waveRaf);
  waveRaf = 0;
  useMindLive.setState({ wavePlaying: false });
  const s = useMindLive.getState();
  if (!on || s.status !== 'on' || (s.kind !== 'wedge' && s.kind !== 'ring')) return;
  const kind = s.kind, start = s.waveValue, started = performance.now();
  const epoch = waveEpoch, gen = generation;
  const pacer = createPacer(CAMERA_HZ);
  useMindLive.setState({ wavePlaying: true });
  const tick = (now: number) => {
    if (epoch !== waveEpoch || gen !== generation) return;
    if (pacer.admit(now)) setWaveValue(waveValueAt(kind, start, now - started));
    waveRaf = requestAnimationFrame(tick);
  };
  waveRaf = requestAnimationFrame(tick);
}

/** Mirror or not: the still is redrawn and pushed again; the camera's next frame follows. */
export async function setMirror(on: boolean): Promise<void> {
  if (['paint', 'wedge', 'ring'].includes(useMindLive.getState().kind ?? '')) return;
  useMindLive.setState({ mirror: on });
  const s = useMindLive.getState();
  if (s.kind === 'image' && still && s.status === 'on') {
    const img = drawInto(still, still.width, still.height, on);
    layer?.push(img, { force: true });
  }
}

/** Stop the frame loop and the camera's tracks; the layer and the square stay for the next source. */
function stopSource(): void {
  setWavePlaying(false);
  waveImage = null;
  if (paintRaf) cancelAnimationFrame(paintRaf);
  paintRaf = 0;
  paintDirty = false;
  paintImage = null;
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  if (vfc && video) (video as VideoWithFrameCallbacks).cancelVideoFrameCallback?.(vfc);
  vfc = 0;
  if (stream) for (const t of stream.getTracks()) t.stop();
  stream = null;
  if (video) {
    video.pause();
    video.srcObject = null;
  }
  video = null;
  still?.close();
  still = null;
}

/** Back to the run's own bar: the source stops, the layer leaves the viewport, the square is forgotten. */
export async function stopLive(): Promise<void> {
  ++generation;
  ++layerEpoch;
  stopSource();
  lastImage = null;
  square = null;
  const l = layer;
  layer = null;
  layerFor = null;
  useMindLive.setState({ kind: null, status: 'off', error: null, frames: 0, label: null, pushed: null });
  const done = layerWork.then(() => l?.dispose()).catch(() => undefined);
  layerWork = done;
  await done;
}
